import { QueryKey } from "@tanstack/react-query";
import { isNumber } from "lodash";

import { getProducts, ProductsResponse } from "../../../../commons/libs/content-service";
import { getProductVariants } from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { ActiveFilters, ProductFilter } from "../../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { Currency } from "../../../../commons/types/currency";
import { AssortmentPriceSettings } from "../../../../commons/types/settings";
import { getProductFiltersFromActiveFilters } from "../filters";
import useQuery, { UseQueryOptions } from "../hooks/use-query";

import { useSettings } from "./use-settings";

type QueryOptions<Type extends ProductType> = Omit<UseQueryOptions<ProductsResponse<Type>>, "queryKey" | "queryFn">;

interface Arguments<Type extends ProductType> {
  productType: Type;
  activeFilters: ActiveFilters<Type>;
  searchTerm?: string;
  brandKey?: string;
  limit?: number;
  offset?: number;
  options?: QueryOptions<Type>;
}

const useProducts = <Type extends ProductType>({
  productType,
  activeFilters,
  searchTerm = "",
  brandKey = "",
  limit = undefined,
  offset = undefined,
  options = {}
}: Arguments<Type>) => {
  const settings = useSettings();

  const specConfig = getSpecConfig(productType);
  const productFilters = getProductFiltersFromActiveFilters(activeFilters, specConfig.filterConfig);
  const evaluatedProductFilters = brandKey
    ? [...productFilters, { key: ProductSpecKey.BrandKey, value: brandKey }]
    : productFilters;

  const query = useQuery({
    ...options,
    gcTime: 0, // Don't use the frontend query cache for these requests.
    queryKey: queryKey({
      brandKey,
      currency: settings.initialized.currency,
      productFilters: evaluatedProductFilters,
      searchTerm,
      assortmentPriceSettings: settings.assortmentPriceSettings,
      limit,
      offset
    }),
    queryFn: () => {
      return getProducts<Type>(
        settings.initialized.currency,
        settings.assortmentPriceSettings,
        evaluatedProductFilters,
        searchTerm,
        limit,
        offset
      );
    }
  });

  const productsLength = query.data?.results.length ?? 0;
  const variantsLength = query.data?.results.flatMap(getProductVariants).length ?? 0;

  return {
    query,
    productsLength,
    variantsLength,
    usedSearchTerm: searchTerm
  };
};

const queryKey = ({
  brandKey,
  currency,
  productFilters,
  searchTerm,
  assortmentPriceSettings,
  limit,
  offset
}: {
  currency: Currency;
  productFilters: ProductFilter[];
  assortmentPriceSettings: AssortmentPriceSettings;
  searchTerm?: string;
  brandKey?: string;
  limit?: number;
  offset?: number;
}): QueryKey => [
  "products",
  ...(!!brandKey ? [brandKey] : []),
  ...(!!searchTerm ? [searchTerm] : []),
  productFilters,
  currency,
  assortmentPriceSettings,
  ...(isNumber(limit) ? [`limit:${limit}`] : []),
  ...(isNumber(offset) ? [`offset:${offset}`] : [])
];

export default useProducts;
